import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

/** splash → exiting (shell reveals) → ready (splash unmounted) */
export type AppBootPhase = "splash" | "exiting" | "ready";

interface AppBootstrapContextValue {
  phase: AppBootPhase;
  isSplashVisible: boolean;
  isShellVisible: boolean;
  /** Force the splash to reveal the shell immediately. */
  completeBootstrap: () => void;
}

const AppBootstrapContext = createContext<AppBootstrapContextValue | null>(null);

const MIN_SPLASH_MS = 2900;
/** Must match splash-screen--exiting fade duration. */
const EXIT_MS = 1900;

interface AppBootstrapProviderProps {
  children: ReactNode;
  /**
   * When false, the splash stays up (e.g. while auth + user profile load).
   * The shell reveals once this is true AND the minimum splash time elapses.
   */
  ready?: boolean;
}

export function AppBootstrapProvider({
  children,
  ready = true,
}: AppBootstrapProviderProps) {
  const [phase, setPhase] = useState<AppBootPhase>("splash");
  const startRef = useRef(Date.now());
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const completeBootstrap = useCallback(() => {
    if (prefersReducedMotion) {
      setPhase("ready");
      return;
    }
    setPhase("exiting");
    window.setTimeout(() => setPhase("ready"), EXIT_MS);
  }, [prefersReducedMotion]);

  useEffect(() => {
    if (!ready) return;

    if (prefersReducedMotion) {
      setPhase("ready");
      return;
    }

    let cancelled = false;
    let exitTimer: number | undefined;
    const elapsed = Date.now() - startRef.current;
    const wait = Math.max(0, MIN_SPLASH_MS - elapsed);

    const startTimer = window.setTimeout(() => {
      if (cancelled) return;
      setPhase("exiting");
      exitTimer = window.setTimeout(() => {
        if (!cancelled) setPhase("ready");
      }, EXIT_MS);
    }, wait);

    return () => {
      cancelled = true;
      window.clearTimeout(startTimer);
      if (exitTimer) window.clearTimeout(exitTimer);
    };
  }, [ready, prefersReducedMotion]);

  const value = useMemo(
    (): AppBootstrapContextValue => ({
      phase,
      isSplashVisible: phase === "splash" || phase === "exiting",
      isShellVisible: phase === "exiting" || phase === "ready",
      completeBootstrap,
    }),
    [phase, completeBootstrap],
  );

  return (
    <AppBootstrapContext.Provider value={value}>{children}</AppBootstrapContext.Provider>
  );
}

export function useAppBootstrap() {
  const ctx = useContext(AppBootstrapContext);
  if (!ctx) {
    throw new Error("useAppBootstrap must be used within AppBootstrapProvider");
  }
  return ctx;
}

export function useAppBootstrapOptional() {
  return useContext(AppBootstrapContext);
}
